import React from 'react';
import { whyChooseUs } from '../mock';
import { Scale, Users, Award, TrendingUp } from 'lucide-react';

const iconComponents = {
  Scale,
  Users,
  Award,
  TrendingUp
};

const AboutSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-[#012169]/5 via-white to-[#F97316]/5 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F97316]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#012169]/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#012169] to-[#013d9e] text-white rounded-full shadow-lg mb-4">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
            </svg>
            <span className="text-sm font-bold uppercase tracking-wider">About Our Service</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#012169] to-[#F97316] mb-4">
            Leading Trademark Registration Partner
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Protecting 10,000+ brands with 98% USPTO approval success
          </p>
        </div>

        {/* Modern Bento Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Large Main Card - Spans 8 columns */}
          <div className="lg:col-span-8 bg-gradient-to-br from-[#012169] to-[#013d9e] rounded-3xl p-8 md:p-12 shadow-2xl transition-all duration-500 group relative overflow-hidden hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(6,182,212,0.4)]">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10 group-hover:opacity-15 transition-opacity duration-500">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F97316] rounded-full blur-3xl"></div>
            </div>
            
            {/* Subtle Top Border Glow */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#F97316] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-[#F97316] rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl group-hover:bg-[#EA580C] transition-all duration-500">
                  <svg className="w-8 h-8 text-white group-hover:scale-110 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white transition-all duration-500">
                  Federal Trademark Protection
                </h3>
              </div>
              
              <p className="text-base text-white/90 leading-relaxed mb-6">
                Your brand identity deserves professional legal protection. We provide comprehensive <span className="font-bold text-[#F97316]">USPTO trademark registration services</span> that give you exclusive nationwide rights to your business name, logo, and slogan.
              </p>
              
              <p className="text-base text-white/80 leading-relaxed mb-8">
                Our licensed IP attorneys have successfully registered <span className="font-bold">10,000+ trademarks</span> with an industry-leading <span className="font-bold text-[#F97316]">98% approval rate</span>. From initial search to final registration certificate, we handle everything.
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4">
                <div className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full border-2 border-white/30">
                  <span className="text-2xl font-black text-white">10K+</span>
                  <span className="text-sm text-white/80 ml-2">Brands Protected</span>
                </div>
                <div className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full border-2 border-white/30">
                  <span className="text-2xl font-black text-[#F97316]">98%</span>
                  <span className="text-sm text-white/80 ml-2">Success Rate</span>
                </div>
                <div className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full border-2 border-white/30">
                  <span className="text-2xl font-black text-white">15+</span>
                  <span className="text-sm text-white/80 ml-2">Years</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Stacked Cards - Spans 4 columns */}
          <div className="lg:col-span-4 space-y-6">
            {whyChooseUs.slice(0, 2).map((feature) => {
              const IconComponent = iconComponents[feature.icon];
              return (
                <div
                  key={feature.id}
                  className="bg-white rounded-3xl p-6 shadow-lg transition-all duration-400 border-2 border-gray-100 group relative overflow-hidden hover:border-[#F97316] hover:-translate-y-1 hover:shadow-xl"
                >
                  {/* Gradient Overlay on Hover */}
                  <div className="absolute bottom-0 left-0 right-0 h-0 bg-gradient-to-t from-[#F97316]/10 to-transparent group-hover:h-full transition-all duration-400 rounded-3xl"></div>
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#F97316] to-[#EA580C] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-400 shadow-lg group-hover:shadow-xl">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-base font-bold text-[#012169] mb-2 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Row - 2 Wide Cards */}
          <div className="lg:col-span-6">
            {whyChooseUs[2] && (
              <div className="bg-gradient-to-br from-[#F97316] to-[#EA580C] rounded-3xl p-8 shadow-2xl transition-all duration-500 h-full group relative overflow-hidden hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(249,115,22,0.5)]">
                {/* Bottom Border Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
                
                <div className="flex items-start gap-4 relative z-10">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-shadow duration-500 flex-shrink-0">
                    {React.createElement(iconComponents[whyChooseUs[2].icon], { className: "h-8 w-8 text-[#F97316] group-hover:scale-110 transition-transform duration-400" })}
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white mb-3 transition-all duration-400">
                      {whyChooseUs[2].title}
                    </h3>
                    <p className="text-white/90 text-base leading-relaxed transition-colors duration-300">
                      {whyChooseUs[2].description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-6">
            {whyChooseUs[3] && (
              <div className="bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] rounded-3xl p-8 shadow-2xl transition-all duration-500 h-full group relative overflow-hidden hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(139,92,246,0.5)]">
                {/* Bottom Border Accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-60 transition-opacity duration-500"></div>
                
                <div className="flex items-start gap-4 relative z-10">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-shadow duration-500 flex-shrink-0">
                    {React.createElement(iconComponents[whyChooseUs[3].icon], { className: "h-8 w-8 text-[#8B5CF6] group-hover:scale-110 transition-transform duration-400" })}
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white mb-3 transition-all duration-400">
                      {whyChooseUs[3].title}
                    </h3>
                    <p className="text-white/90 text-base leading-relaxed transition-colors duration-300">
                      {whyChooseUs[3].description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;